Retorna a lista paginada de chamadas da organização autenticada.

É o endpoint principal para histórico operacional, reconciliação com sistemas externos e monitoramento de outcomes.

<ApiCodeExamples exampleId="callsList" />

<ApiResponseExamples exampleId="callsList" />

## Query parameters

- `page` (`integer`, opcional): número da página.
  Regra:
  começa em `1`
- `page_size` (`integer`, opcional): quantidade de itens por página.
  Regra:
  default `25`, máximo `100`
- `ordering` (`string`, opcional): campo de ordenação. Use prefixo `-` para ordem decrescente.
  Valores possíveis:
  `created_at`, `updated_at`, `initiated_at`, `answered_at`, `ended_at`, `duration`, `status`
- `search` (`string`, opcional): busca textual aplicada em números, agente, campanha, contato e identificadores públicos relacionados.
- `status` (`string`, opcional): um ou mais status, separados por vírgula.
  Valores possíveis:
  `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`
- `direction` (`string`, opcional): um ou mais sentidos de chamada, separados por vírgula.
  Valores possíveis:
  `outbound`, `inbound`
- `origin` (`string`, opcional): uma ou mais origens, separadas por vírgula.
  Valores possíveis:
  `campaign`, `manual`, `public_api`
- `call_type` (`string`, opcional): um ou mais tipos, separados por vírgula.
  Valores possíveis:
  `ai_agent`, `reverse_ivr`, `sip`
- `campaign_id` (`uuid`, opcional): identificador da campanha para restringir a listagem.
- `campaign_contact_id` (`uuid`, opcional): identificador do contato da campanha para restringir a listagem.
- `public_api_call_contact_id` (`uuid`, opcional): identificador do job pai criado por `POST /public/v1/calls/`.
- `public_api_call_contact_phone_id` (`uuid`, opcional): identificador do telefone/tentativa dentro do job público.
- `public_api_call_contact_status` (`string`, opcional): um ou mais status do job pai público.
- `public_api_call_contact_phone_status` (`string`, opcional): um ou mais status do telefone dentro do job público.
- `outcome` (`string`, opcional): um ou mais outcomes semânticos, separados por vírgula.
  Valores possíveis:
  `converted`, `mismatch`, `optout`, `ivr_heard_to_end`
- `is_converted` (`boolean`, opcional): filtra chamadas convertidas.
- `is_optout` (`boolean`, opcional): filtra chamadas com opt-out.
- `is_mismatch` (`boolean`, opcional): filtra chamadas com mismatch.
- `has_transfer` (`boolean`, opcional): filtra chamadas que iniciaram transferência humana.
- `has_human_answer` (`boolean`, opcional): filtra chamadas em que a transferência humana foi atendida.
- `has_ai_answer` (`boolean`, opcional): filtra chamadas que tiveram segmento de IA conectado.
- `has_ai_refund` (`boolean`, opcional): filtra chamadas com estorno de segundos de IA.
- `ivr_selected_digit` (`string`, opcional): um ou mais dígitos DTMF selecionados, separados por vírgula.
- `participant_user` (`uuid`, opcional): um ou mais usuários participantes/atendentes, separados por vírgula.
- `region` (`string`, opcional): uma ou mais regiões do número de destino, separadas por vírgula.
- `state_code` (`string`, opcional): uma ou mais UFs do número de destino, separadas por vírgula.
- `ddd` (`string`, opcional): um ou mais DDDs brasileiros, separados por vírgula.
- `phone_type` (`string`, opcional): um ou mais tipos de telefone, separados por vírgula.
  Valores possíveis:
  `unknown`, `mobile`, `landline`, `sip`
- `start_date` (`date|datetime`, opcional): início do período pela atividade da chamada.
- `end_date` (`date|datetime`, opcional): fim do período pela atividade da chamada.

Para os filtros booleanos, a API aceita:

- verdadeiros: `1`, `true`, `yes`, `on`, `sim`
- falsos: `0`, `false`, `no`, `off`, `nao`, `não`

## Resposta `200`

### Paginação

- `count` (`integer`): total de chamadas que batem com os filtros.
- `next` (`string|null`): URL completa da próxima página.
- `previous` (`string|null`): URL completa da página anterior.
- `results` (`array<object>`): itens da página atual.

### Campos de cada item em `results`

- `id` (`uuid`): identificador da chamada.
- `canonical_url` (`string`): link canônico da chamada no app Contact-Tel.
- `direction` (`string`): sentido da chamada.
  Valores possíveis:
  `outbound`, `inbound`
- `origin` (`string`): origem operacional da chamada.
  Valores possíveis:
  `campaign`, `manual`, `public_api`
- `call_type` (`string`): modalidade da chamada.
  Valores possíveis:
  `ai_agent`, `reverse_ivr`, `sip`
- `status` (`string`): status operacional da chamada.
  Valores possíveis:
  `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`
- `to_number` (`string`): destino principal da chamada.
- `from_number` (`string`): origem da chamada.
- `duration` (`integer`): duração líquida em segundos.
- `ai_billed_seconds` (`integer`): segundos faturáveis líquidos de IA.
- `ai_gross_billed_seconds` (`integer`): segundos faturáveis brutos de IA.
- `ai_refunded_seconds` (`integer`): segundos de IA estornados/refundados.
- `telephony_billed_seconds` (`integer`): segundos faturáveis líquidos de telefonia.
- `telephony_gross_billed_seconds` (`integer`): segundos faturáveis brutos de telefonia.
- `telephony_refunded_seconds` (`integer`): segundos de telefonia estornados/refundados.
- `is_converted` (`boolean`): indica se a chamada foi convertida.
- `is_optout` (`boolean`): indica se a chamada terminou com opt-out.
- `is_mismatch` (`boolean`): indica se a chamada terminou com mismatch.
- `initiated_at` (`datetime|null`): instante da iniciação da chamada.
- `answered_at` (`datetime|null`): instante do atendimento/conexão.
- `ended_at` (`datetime|null`): instante do término.
- `created_at` (`datetime`): instante de criação.
- `updated_at` (`datetime`): instante da última atualização.
- `campaign_id` (`uuid|null`): identificador da campanha associada.
- `campaign_name` (`string|null`): nome da campanha associada.
- `campaign_contact_id` (`uuid|null`): identificador do contato associado.
- `campaign_contact_display_name` (`string|null`): nome exibido do contato associado.
- `public_api_call_contact_id` (`uuid|null`): job pai público associado, quando a chamada veio da API pública.
- `client_reference` (`string|null`): referência opaca definida pelo cliente no job pai, ou `null` para chamadas sem esse vínculo público.
- `public_api_call_contact_status` (`string|null`): status atual do job pai público.
- `retry_policy` (`object|null`): snapshot da política durável do job pai, ou `null` quando a chamada pertence ao fluxo legado ou não veio da API pública.
- `attempts` (`object|null`): progresso agregado do job pai, com `count`, `remaining` e `next_attempt_at`.
- `public_api_call_contact_phone_id` (`uuid|null`): telefone/tentativa do job público associado.
- `public_api_call_contact_phone_status` (`string|null`): status atual do telefone dentro do job público.
- `public_api_call_contact_phone_position` (`integer|null`): posição do telefone no job público.
- `agent_id` (`uuid|null`): identificador do agente vinculado à chamada.
- `agent_name` (`string|null`): nome do agente vinculado.
- `created_by_id` (`uuid|null`): identificador do usuário que criou/originou a chamada, quando existir.
- `created_by_name` (`string|null`): nome exibível do usuário que criou/originou a chamada.
- `created_by_extension` (`string|null`): ramal do usuário criador/originador, quando existir.
- `telephony_transfer_started_at` (`datetime|null`): instante em que a transferência humana foi iniciada.
- `telephony_transfer_wait_started_at` (`datetime|null`): instante em que a chamada entrou em espera por humano.
- `telephony_transfer_connected_at` (`datetime|null`): instante em que a perna humana foi conectada.
- `telephony_transfer_wait_ms` (`integer|null`): tempo de espera por humano em milissegundos.
- `transfer_destination_type` (`string|null`): tipo do destino usado na transferência.
- `transfer_destination_value` (`string|null`): ramal ou destino usado na transferência.
- `transfer_target_user_id` (`uuid|null`): identificador do usuário alvo da transferência.
- `transfer_target_user_name` (`string|null`): nome exibível do usuário alvo.
- `transfer_target_user_extension` (`string|null`): ramal do usuário alvo.
- `transfer_target_group_id` (`uuid|null`): identificador do grupo alvo da transferência.
- `transfer_target_group_name` (`string|null`): nome exibível do grupo alvo.
- `interaction_claimed_by_id` (`uuid|null`): usuário que assumiu a interação humana.
- `interaction_claimed_by_name` (`string|null`): nome exibível de quem assumiu a interação.
- `interaction_claimed_at` (`datetime|null`): instante do claim.
- `interaction_answered_by_id` (`uuid|null`): usuário que atendeu a interação humana.
- `interaction_answered_by_name` (`string|null`): nome exibível de quem atendeu.
- `interaction_answered_at` (`datetime|null`): instante do atendimento humano.
- `interaction_answered_extension` (`string|null`): ramal que atendeu a interação humana.

### Referência de opções

#### `direction`

- `outbound`: chamada originada pela plataforma para um destino externo.
- `inbound`: chamada recebida pela plataforma.

#### `origin`

- `campaign`: chamada criada por campanha.
- `manual`: chamada iniciada manualmente por usuário/operador.
- `public_api`: chamada criada por integração via API pública.

#### `call_type`

- `ai_agent`: chamada conduzida por agente de IA.
- `reverse_ivr`: chamada de URA reversa com áudio e captura DTMF.
- `sip`: chamada SIP convencional sem agente de IA como condutor principal.

#### `outcome`

- `converted`: chamada marcada como convertida.
- `mismatch`: pessoa errada ou contato divergente.
- `optout`: pedido explícito de remoção/parada de contato.
- `ivr_heard_to_end`: chamada Reverse IVR em que o áudio foi ouvido até o fim.

#### `to_number_phone_type`

- `unknown`: tipo do destino não resolvido.
- `mobile`: número móvel.
- `landline`: telefone fixo.
- `sip`: ramal/endereço SIP.

#### `status`

- `pending`: chamada criada e aguardando enfileiramento.
- `queued`: chamada na fila de execução.
- `dialing`: discagem em andamento.
- `analyzing`: etapa pré-atendimento/análise em andamento.
- `ringing`: destino tocando.
- `in_progress`: chamada atendida e em andamento.
- `finalizing`: chamada encerrando e consolidando dados.
- `completed`: chamada concluída com sucesso operacional.
- `no_interaction`: houve atendimento, mas sem interação útil do usuário.
- `failed`: falha operacional genérica.
- `no_answer`: destino não atendeu.
- `no_answer_timeout`: timeout aguardando atendimento.
- `busy`: destino ocupado.
- `cancelled`: chamada cancelada antes de concluir.
- `transfer_unanswered`: transferência não atendida.
- `congestion`: rede/rota congestionada.
- `voicemail`: chamada caiu em caixa postal.
- `invalid_number`: número inválido ou inexistente.
- `config_error`: erro de configuração da campanha, agente ou telefonia.
- `declined`: destino recusou a chamada.
- `unreachable`: destino temporariamente indisponível.
- `timeout`: timeout técnico da requisição/chamada.
- `mismatch`: pessoa errada atendeu.
- `api_timeout`: timeout de API/provider durante a execução.
- `terminated_manual`: encerramento manual por operador/admin.
- `waiting_capacity`: chamada aceita pela API pública e aguardando slot de concorrência.
- `waiting_operating_hours`: chamada aceita pela API pública e aguardando horário operacional.
- `capacity_timeout`: chamada pública expirou aguardando capacidade.
- `operating_window_timeout`: chamada pública expirou aguardando janela operacional.

## Erros esperados

- `401 Unauthorized`: token inválido, expirado, revogado, ausente ou bloqueado por IP.
- `403 Forbidden`: a credencial autenticou, mas não possui `calls.read`.
- `429 Too Many Requests`: a credencial excedeu o limite permitido na janela atual.

## Quando usar este endpoint

- acompanhar histórico de chamadas por campanha;
- alimentar monitoramento externo;
- sincronizar desfechos operacionais de chamadas em sistemas do cliente.
